import * as React from 'react';
import { HelpServiceChannel } from '@tableau/search-microview-communication-channels';
import { InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import { Messages } from '../../../../../gen-src/Localize';
import { Metric } from '../../../models/SearchResultTypes';
import { SessionManagementService } from '../../../services/SessionManagementService';
import { DetailsTooltipStyles } from '../../tooltips/DetailsTooltipStyles';
import { BasicField } from '../../tooltips/elements/BasicField';
import { MarkupField } from '../../tooltips/elements/MarkupField';
import { Name } from '../../tooltips/elements/Name';
import { Tags } from '../../tooltips/elements/Tags';
import { Timestamp } from '../../tooltips/elements/Timestamp';
import { ContentTooltipProps, TooltipDescriptionTestId } from '../../tooltips/TooltipHelper';

export interface MetricCardOwnProps {
  isOwner: boolean;
  refreshedAt?: Date;
  hasMetricData: boolean;
}
export type MetricTooltipProps = ContentTooltipProps & MetricCardOwnProps;

export function MetricTooltip(props: MetricTooltipProps): JSX.Element {
  const { content, refreshedAt } = props;
  const metricContent = content as Metric;

  return (
    <div role={'tooltip'}>
      <Name name={metricContent.name} />
      {metricContent.suspended && <MetricSuspendedWarning {...props} />}
      {metricContent.description && (
        <MarkupField
          markup={metricContent.description}
          testId={TooltipDescriptionTestId}
          extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]}
        />
      )}
      {metricContent.tags && <Tags tags={metricContent.tags} />}
      {metricContent.hitsTotal !== undefined && <BasicField name={Messages.views()} value={metricContent.hitsTotal} />}
      {refreshedAt && <Timestamp label={Messages.column_metrics_lastUpdated()} value={refreshedAt} />}
    </div>
  );
}

function MetricSuspendedWarning(props: MetricTooltipProps): JSX.Element {
  const [href, setHref] = React.useState('');

  const inlineAlertProps = {
    testId: 'metrics-suspended',
    type: InlineAlertType.Warning,
    style: { marginBottom: '18px' },
  };

  if (userCanResumeSuspendedMetric()) {
    return <InlineAlertWidget {...inlineAlertProps}>{renderSuspendedWarningWithHelpService()}</InlineAlertWidget>;
  } else {
    return <InlineAlertWidget {...inlineAlertProps}>{Messages.metricSuspendedNoResumeTooltip()}</InlineAlertWidget>;
  }

  function userCanResumeSuspendedMetric(): boolean {
    if (!props.hasMetricData) {
      return false;
    }
    return (
      props.isOwner || SessionManagementService.instance.isSessionServerAdmin() || SessionManagementService.instance.isSessionSiteAdmin()
    );
  }

  function renderSuspendedWarningWithHelpService(): JSX.Element {
    const helpServiceChannel = new HelpServiceChannel();
    helpServiceChannel.call({ category: 'metrics-suspend' }).data?.then((result) => {
      setHref(result);
    });
    return (
      <>
        {Messages.metricSuspendedWithResumeTooltip()}
        <a href={href} target="_blank" style={{ fontWeight: 'bold', paddingLeft: '5px' }} rel="noreferrer">
          {Messages.learnMore()}
        </a>
      </>
    );
  }
}
